import { useMemo, useState } from "react";
import { Link, useLocation, useParams } from "wouter";
import { ArrowRight, ClipboardCheck, History, Pencil, Plus, Trash2, X, Save } from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Badge, Card, PrimaryButton, SectionTitle } from "@/components/Card";
import { StandardTabsHeader } from "@/components/StandardTabsHeader";
import { useDemoStore } from "@/store/useDemoStore";
import { useStandard, useElementsForStandard, useInstrumentsForStandard, useRulesForStandard } from "@/store/hooks";
import type { Element as StandardElement, Standard } from "@/data";

export default function StandardDetail() {
  const params = useParams<{ codigo: string }>();
  const codigo = params.codigo ?? "";
  const [, setLocation] = useLocation();

  const standard = useStandard(codigo);
  const elems = useElementsForStandard(codigo);
  const instruments = useInstrumentsForStandard(codigo);
  const auditLog = useDemoStore((s) => s.auditLog);
  const activeRole = useDemoStore((s) => s.activeRole);

  const updateStandard = useDemoStore((s) => s.updateStandard);
  const removeStandard = useDemoStore((s) => s.removeStandard);
  const upsertElement = useDemoStore((s) => s.upsertElement);
  const removeElement = useDemoStore((s) => s.removeElement);
  const logEvent = useDemoStore((s) => s.logEvent);

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Partial<Standard> | null>(null);

  const history = useMemo(
    () => auditLog.filter((e) => e.entidad === "standard" && e.entidadId === codigo).slice(0, 25),
    [auditLog, codigo],
  );

  if (!standard) {
    return (
      <AppLayout breadcrumb={["Estándares", codigo]}>
        <Card>
          <SectionTitle>Estándar no encontrado</SectionTitle>
          <div style={{ fontSize: 13, color: "var(--cc-gray-600)", marginBottom: 12 }}>
            No existe un estándar con código <strong>{codigo}</strong> en el catálogo actual.
          </div>
          <Link href="/admin/estandares" data-testid="link-back-list">
            <PrimaryButton variant="outline">Volver al catálogo</PrimaryButton>
          </Link>
        </Card>
      </AppLayout>
    );
  }

  function startEdit() {
    setDraft({
      titulo: standard!.titulo,
      sector: standard!.sector,
      comite: standard!.comite,
      vigencia: standard!.vigencia,
      tipo: standard!.tipo,
      version: standard!.version,
      periodoRevision: standard!.periodoRevision,
      descripcion: standard!.descripcion,
      proposito: standard!.proposito,
    });
    setEditing(true);
  }

  function cancelEdit() { setDraft(null); setEditing(false); }

  function saveEdit() {
    if (!draft) return;
    updateStandard(codigo, { ...draft, ultimaActualizacion: today() });
    logEvent({ actor: "Administrador académico", rol: activeRole, accion: "editar", entidad: "standard", entidadId: codigo });
    setEditing(false);
    setDraft(null);
  }

  function addNewElement() {
    const nextNum = elems.length + 1;
    const proposedCode = window.prompt("Código del elemento (ej. E5639):", `E${5636 + nextNum}`);
    if (!proposedCode) return;
    const titulo = window.prompt("Título del elemento:", "Nuevo elemento de competencia");
    if (!titulo) return;
    const nuevo: StandardElement = {
      id: `elem-${proposedCode.toLowerCase()}-${Date.now().toString(36).slice(-4)}`,
      codigo: proposedCode.trim().toUpperCase(),
      estandar: codigo,
      titulo: titulo.trim(),
      descripcion: "",
      criterios: [],
      evidencias: [],
      instrumentos: [],
    };
    upsertElement(codigo, nuevo);
    updateStandard(codigo, { elementos: elems.length + 1, ultimaActualizacion: today() });
    logEvent({ actor: "Administrador académico", rol: activeRole, accion: "agregar-elemento", entidad: "standard", entidadId: codigo });
    setLocation(`/admin/estandares/${codigo}/elementos/${nuevo.id}/criterios`);
  }

  function deleteElement(e: StandardElement) {
    if (!window.confirm(`¿Eliminar el elemento ${e.codigo}? Esta acción no se puede deshacer.`)) return;
    removeElement(codigo, e.id);
    const remaining = elems.filter((x) => x.id !== e.id);
    updateStandard(codigo, {
      elementos: remaining.length,
      criterios: remaining.reduce((a, x) => a + x.criterios.length, 0),
      ultimaActualizacion: today(),
    });
    logEvent({ actor: "Administrador académico", rol: activeRole, accion: "eliminar-elemento", entidad: "standard", entidadId: codigo });
  }

  const isPublished = standard.estado === "publicado";

  return (
    <AppLayout breadcrumb={["Estándares", standard.codigo]}>
      <StandardTabsHeader standard={standard} active="datos" />

      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Card>
            <SectionTitle action={editing ? (
              <div style={{ display: "flex", gap: 8 }}>
                <PrimaryButton size="sm" variant="ghost" onClick={cancelEdit} testId="button-cancel-edit"><X size={13} /> Cancelar</PrimaryButton>
                <PrimaryButton size="sm" variant="dark" onClick={saveEdit} testId="button-save-edit"><Save size={13} /> Guardar</PrimaryButton>
              </div>
            ) : (
              <PrimaryButton size="sm" variant="outline" onClick={startEdit} testId="button-edit"><Pencil size={13} /> Editar</PrimaryButton>
            )}>Datos generales</SectionTitle>

            {editing && draft ? (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <Field label="Título" full value={draft.titulo ?? ""} onChange={(v) => setDraft({ ...draft, titulo: v })} testId="edit-titulo" />
                <Field label="Sector" value={draft.sector ?? ""} onChange={(v) => setDraft({ ...draft, sector: v })} testId="edit-sector" />
                <Field label="Vigencia" value={draft.vigencia ?? ""} onChange={(v) => setDraft({ ...draft, vigencia: v })} testId="edit-vigencia" />
                <Field label="Comité" full value={draft.comite ?? ""} onChange={(v) => setDraft({ ...draft, comite: v })} testId="edit-comite" />
                <Field label="Tipo" value={draft.tipo ?? ""} onChange={(v) => setDraft({ ...draft, tipo: v })} testId="edit-tipo" />
                <Field label="Versión" value={draft.version ?? ""} onChange={(v) => setDraft({ ...draft, version: v })} testId="edit-version" />
                <Field label="Periodo de revisión" value={draft.periodoRevision ?? ""} onChange={(v) => setDraft({ ...draft, periodoRevision: v })} testId="edit-periodo" />
                <Field label="Descripción" full multiline value={draft.descripcion ?? ""} onChange={(v) => setDraft({ ...draft, descripcion: v })} testId="edit-descripcion" />
                <Field label="Propósito" full multiline value={draft.proposito ?? ""} onChange={(v) => setDraft({ ...draft, proposito: v })} testId="edit-proposito" />
              </div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <ReadOnly label="Título" value={standard.titulo} full />
                <ReadOnly label="Sector" value={standard.sector} />
                <ReadOnly label="Vigencia" value={standard.vigencia} />
                <ReadOnly label="Comité" value={standard.comite ?? "—"} full />
                {standard.tipo && <ReadOnly label="Tipo" value={standard.tipo} />}
                {standard.version && <ReadOnly label="Versión" value={standard.version} />}
                {standard.periodoRevision && <ReadOnly label="Periodo de revisión" value={standard.periodoRevision} />}
                {standard.descripcion && <ReadOnly label="Descripción" value={standard.descripcion} full />}
                {standard.proposito && <ReadOnly label="Propósito" value={standard.proposito} full />}
                {standard.documentoFuente && (
                  <ReadOnly
                    label="Documento fuente"
                    value={`${standard.documentoFuente.nombre} · ${standard.documentoFuente.tamanioKb} KB · cargado ${standard.documentoFuente.fechaCarga}`}
                    full
                  />
                )}
              </div>
            )}
          </Card>

          <Card>
            <SectionTitle action={
              <PrimaryButton size="sm" variant="outline" onClick={addNewElement} testId="button-add-element"><Plus size={13} /> Nuevo elemento</PrimaryButton>
            }>Elementos del estándar</SectionTitle>
            {elems.length === 0 ? (
              <div style={{ fontSize: 13, color: "var(--cc-gray-500)" }}>Este estándar aún no tiene elementos. Crea el primero con el botón superior.</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {elems.map((e) => (
                  <div key={e.id} style={{ padding: 14, border: "1px solid var(--cc-gray-200)", borderRadius: 10, display: "flex", gap: 12, alignItems: "flex-start" }}>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                      <div style={{ background: "var(--cc-teal-bg)", color: "var(--cc-teal)", padding: "6px 10px", borderRadius: 8, fontWeight: 700, fontSize: 12 }}>{e.codigo}</div>
                      {typeof e.orden === "number" && (
                        <div style={{ fontSize: 10, color: "var(--cc-gray-500)" }}>Orden {e.orden}</div>
                      )}
                    </div>
                    <Link href={`/admin/estandares/${codigo}/elementos/${e.id}/criterios`} data-testid={`link-element-${e.codigo}`} style={{ flex: 1, textDecoration: "none", color: "inherit", cursor: "pointer" }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: "var(--cc-navy)", marginBottom: 4 }}>{e.titulo}</div>
                      <div style={{ fontSize: 12, color: "var(--cc-gray-600)", lineHeight: 1.5 }}>{e.descripcion}</div>
                      {e.tipoEvaluacionPrincipal && (
                        <div style={{ marginTop: 8, display: "inline-flex", gap: 6, alignItems: "center" }}>
                          <Badge tone="teal">Evaluación</Badge>
                          <span style={{ fontSize: 11, color: "var(--cc-gray-700)" }}>{e.tipoEvaluacionPrincipal}</span>
                          {e.estadoElemento && <Badge tone={e.estadoElemento === "activo" ? "green" : "amber"}>{e.estadoElemento}</Badge>}
                        </div>
                      )}
                      <div style={{ marginTop: 8, display: "flex", gap: 12, fontSize: 11, color: "var(--cc-gray-500)", flexWrap: "wrap" }}>
                        <span><strong>{e.criterios.length}</strong> criterios</span>
                        <span><strong>{e.evidencias.length}</strong> instrumentos p/evidencias</span>
                        <span><strong>{instruments.filter((i) => i.elementoId === e.id).length}</strong> instrumentos</span>
                        {e.glosario && e.glosario.length > 0 && (
                          <span><strong>{e.glosario.length}</strong> términos en glosario</span>
                        )}
                      </div>
                      {e.glosario && e.glosario.length > 0 && (
                        <details style={{ marginTop: 10 }} data-testid={`glosario-${e.codigo}`}>
                          <summary style={{ cursor: "pointer", fontSize: 11, color: "var(--cc-teal)", fontWeight: 600 }}>Ver glosario ({e.glosario.length} términos)</summary>
                          <div style={{ marginTop: 8, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, fontSize: 11, color: "var(--cc-gray-700)", lineHeight: 1.5, paddingTop: 6, borderTop: "1px dashed var(--cc-gray-200)" }}>
                            {e.glosario.map((g, i) => (
                              <div key={i}><strong style={{ color: "var(--cc-navy)" }}>{g.termino}.</strong> {g.definicion}</div>
                            ))}
                          </div>
                        </details>
                      )}
                    </Link>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      <Link href={`/admin/estandares/${codigo}/elementos/${e.id}/criterios`} data-testid={`edit-element-${e.codigo}`}>
                        <PrimaryButton size="sm" variant="ghost"><Pencil size={12} /> Editar</PrimaryButton>
                      </Link>
                      <button onClick={() => deleteElement(e)} data-testid={`del-element-${e.codigo}`} style={{ background: "transparent", border: "none", color: "var(--cc-gray-500)", cursor: "pointer", fontSize: 11, display: "inline-flex", alignItems: "center", gap: 4 }}>
                        <Trash2 size={12} /> Eliminar
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Card>
            <SectionTitle>Resumen</SectionTitle>
            <Stat label="Elementos" value={String(elems.length || standard.elementos)} />
            <Stat label="Criterios totales" value={String(elems.reduce((a, e) => a + e.criterios.length, 0) || standard.criterios)} />
            <Stat label="Candidatos activos" value={String(standard.candidatosActivos)} />
            <Stat label="Certificados emitidos" value={standard.certificadosEmitidos.toLocaleString("es-MX")} />
            <div style={{ marginTop: 14, padding: 12, background: "var(--cc-teal-bg)", borderRadius: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "var(--cc-teal)", fontWeight: 700, marginBottom: 4 }}>
                <ClipboardCheck size={13} /> SUGERENCIA IA
              </div>
              <div style={{ fontSize: 12, color: "var(--cc-gray-700)", lineHeight: 1.5 }}>
                {isPublished ? "El estándar está publicado: puedes programar nuevas generaciones." : "Cuando termines de configurar elementos y reglas, publícalo para habilitar inscripciones."}
              </div>
            </div>
          </Card>

          <Card>
            <SectionTitle>Historial</SectionTitle>
            {history.length === 0 ? (
              <div style={{ fontSize: 12, color: "var(--cc-gray-500)" }}>Aún no hay eventos registrados para este estándar.</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {history.map((h) => (
                  <div key={h.id} data-testid={`history-${h.id}`} style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "6px 0", borderBottom: "1px solid var(--cc-gray-100)" }}>
                    <History size={12} color="var(--cc-gray-400)" style={{ marginTop: 3 }} />
                    <div style={{ flex: 1, fontSize: 12 }}>
                      <div style={{ fontWeight: 600, color: "var(--cc-navy)", textTransform: "capitalize" }}>{h.accion}</div>
                      <div style={{ color: "var(--cc-gray-500)" }}>{new Date(h.ts).toLocaleString("es-MX")} · {h.actor}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>
    </AppLayout>
  );
}

function today() { return new Date().toISOString().slice(0, 10); }

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid var(--cc-gray-100)" }}>
      <span style={{ fontSize: 12, color: "var(--cc-gray-500)" }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 600, color: "var(--cc-navy)" }}>{value}</span>
    </div>
  );
}

function Field({ label, value, onChange, full, testId, multiline }: { label: string; value: string; onChange: (v: string) => void; full?: boolean; testId?: string; multiline?: boolean }) {
  const inputStyle = { padding: "9px 12px", border: "1px solid var(--cc-gray-200)", borderRadius: 8, fontSize: 13, color: "var(--cc-gray-800)", fontFamily: "inherit" as const };
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4, gridColumn: full ? "1 / -1" : undefined }}>
      <span style={{ fontSize: 11, fontWeight: 600, color: "var(--cc-gray-500)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</span>
      {multiline ? (
        <textarea value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} rows={4} style={{ ...inputStyle, resize: "vertical", lineHeight: 1.5 }} />
      ) : (
        <input value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} style={inputStyle} />
      )}
    </label>
  );
}

function ReadOnly({ label, value, full }: { label: string; value: string; full?: boolean }) {
  return (
    <div style={{ padding: 10, background: "var(--cc-gray-50)", borderRadius: 8, gridColumn: full ? "1 / -1" : undefined }}>
      <div style={{ fontSize: 11, color: "var(--cc-gray-500)", textTransform: "uppercase", fontWeight: 600, letterSpacing: "0.04em" }}>{label}</div>
      <div style={{ fontSize: 13, color: "var(--cc-gray-800)", marginTop: 2 }}>{value}</div>
    </div>
  );
}
